import { Intent } from "@blueprintjs/core";
import { FormTextInput } from "components/form-fields/FormTextInput";
import { Numeric } from "components/formatting";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { UserAccess } from "components/user-access";
import { useDebounce } from "hooks/useDebounce/useDebounce";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { DocumentsService } from "services/DocumentsService";
import { parseBytesTo } from "utils/parseBytesTo";

export function StorageOverviewListing(props) {
    const requiredAction = 'Internal';

    const defaultRecordSize = 25;
    const debounceTimeout = 750;

    const [initalSetupComplete, setInitalSetupComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const baseHeaders = [
        "Account Name",
        "Member Number",
        "No. of Docs",
        "Total Size"
    ];

    const baseSortableHeaders = [
        { headerName: "Account Name", sortName: "AccountName" },
        { headerName: "Member Number", sortName: "MemberNumber" },
        { headerName: "No. of Docs", sortName: "NumberOfDocuments" },
        { headerName: "Total Size", sortName: "TotalSize" }
    ];

    const [sortableHeaders, setSortableHeaders] = useState(baseSortableHeaders);

    const [sortedDir, setSortedDir] = useState("D");
    const [sortedBy, setSortedBy] = useState(sortableHeaders.find(s => s.headerName === "Total Size"));

    const [headers, setHeaders] = useState(baseHeaders);

    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [tableRows, setTableRows] = useState([]);
    
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentPageNumber = useRef();
    const currentSearchTerm = useRef();

    const [documentAreas, setDocumentAreas] = useState([]);

    const dispatch = useDispatch();

    useEffect(() => {
        if (documentAreas.length) {
            const areaHeaders = documentAreas.flatMap(area => ([
                `${area.name} No. of Docs`,
                `${area.name} Total Size`
            ]));

            const areaSortableHeaders = documentAreas.flatMap(area => ([
                { headerName: `${area.name} No. of Docs`, sortName: `NumberOfDocuments-Area-${area.id}`, areaId: area.id },
                { headerName: `${area.name} Total Size`, sortName: `TotalSize-Area-${area.id}`, areaId: area.id }
            ]));

            setHeaders([...baseHeaders, ...areaHeaders]);
            setSortableHeaders([...baseSortableHeaders, ...areaSortableHeaders]);
        }
    }, [documentAreas]);

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function fetchTableData(forceRefresh) {
        if (initalSetupComplete) {
            if (
                currentTableSortDir.current === sortedDir &&
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentSearchTerm.current === debouncedSearchTerm &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentTableSortBy.current = sortedBy;
            currentPageNumber.current = pageNumber;
            currentTableSortDir.current = sortedDir;
            currentSearchTerm.current = debouncedSearchTerm;

            setIsLoading(true);

            const searchRequest = {
                requestCount: defaultRecordSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: [],
                searchTerm: debouncedSearchTerm,
            };

            DocumentsService.getAccountsStorageOverview(searchRequest).then(
                (r) => {
                    let data = [];

                    if (r?.data != null) {
                        data = r.data.map((d) => {
                            const baseData = [
                                <Link key={d.accountId} to={`/storage-overview/${d.accountId}`} state={{ name: d.accountName }}>{d.accountName}</Link>,
                                d.memberNumber,
                                <Numeric key={d.accountId}>{d.numberOfDocuments}</Numeric>,
                                parseBytesTo(d.totalSize).auto()
                            ];

                            documentAreas.forEach(area => {
                                const areaData = d.areas.find(da => da.areaId === area.id);
                                baseData.push(<Numeric>{areaData ? areaData.numberOfDocuments : 0}</Numeric>);
                                baseData.push(areaData ? parseBytesTo(areaData.totalSize).auto() : '0 B');
                            });

                            return baseData;
                        });
                    }

                    setTableRows(data);
                    setTotalRecords(r.totalCount);
                    setIsLoading(false);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not retrieve storage overview, please try again"
                    );
                }
            );
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        initalSetupComplete
    ]);

    useEffect(() => {
        DocumentsService.getDocumentOptions().then(
            (r) => {
                setDocumentAreas(r.documentAreas);
                setInitalSetupComplete(true);
            }
        );

        dispatch({ type: 'SITE_FULL_WIDTH' });
    }, []);

    return <UserAccess 
        perform={requiredAction}
        yes={() => 
            <div className='row'>

                <h1>Storage Overview</h1>
                <div className="intro-text">
                    <p>From here you can view an overview of storage usage across accounts.</p>
                </div>
                
                <div
                    className={"inline-items"}
                >
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search Accounts"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="document-search-field"
                            />
                        </div>
                    </div>
                </div>
                <div className="spacer-top-small">
                    <ListingTable
                        showPerPage={false}
                        pageSize={defaultRecordSize}
                        headers={headers}
                        data={tableRows}
                        loadingData={isLoading}
                        onPagingChange={setPageNumber}
                        totalRecordCount={totalRecords}
                        sortable
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        sortableHeaders={sortableHeaders}
                        onSort={onTableSort}
                    />
                </div>
            </div> 
        }
    />
}